/**
 * Client API for administrator-issued sign-in links
 */
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth, getSignInLinkSenderAuth } from '@/lib/firebase-config';
import { apiFetch } from '@/lib/api-client';
import {
  logSignInLinkCopyInfo,
  logSignInLinkCopyWarn,
} from '@/lib/sign-in-link-copy-log';
import {
  createUsersRolesTrace,
  logUsersRolesDebug,
} from '@/lib/users-roles-debug';
import logger from '@/lib/logging';
import { SELF_SERVE_GENERIC_FAILURE_MESSAGE } from '@/lib/sign-in-link-self-serve-public';

const SIGN_IN_LINK_COPY_SLOW_MS = 2000;

export type SignInLinkApiError = {
  success: false;
  error?: string;
  message?: string;
};

export type CopySignInLinkResponse =
  | SignInLinkApiError
  | {
      success: true;
      message?: string;
      link?: string;
      continueUrl?: string;
      note?: string;
    };

export type PrepareEmailSendResponse =
  | SignInLinkApiError
  | {
      success: true;
      email: string;
      actionCodeSettings: { url: string; handleCodeInApp: boolean };
      nonce: string;
    };

async function parseJsonResponse<T>(response: Response): Promise<T & { error?: string; message?: string }> {
  return (await response.json()) as T & { error?: string; message?: string };
}

function selfServeErrorFromResponse(
  response: Response,
  data: { success?: boolean; message?: string; error?: string },
  fallbackMessage: string
): { success: false; error: string; message: string } {
  const opaquePolicy = response.status === 403 && data.success === false && data.message;
  return {
    success: false,
    error: opaquePolicy ? 'POLICY_REJECTION' : data.error || 'REQUEST_FAILED',
    message: opaquePolicy ? data.message! : data.message || fallbackMessage,
  };
}

const SIGN_IN_EMAIL_SEND_TIMEOUT_MS = 30_000;

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  timeoutMessage: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), ms);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}

export async function requestAdminCopySignInLink(email: string): Promise<CopySignInLinkResponse> {
  const traceId = createUsersRolesTrace('adminCopySignInLink');
  const started = performance.now();
  try {
    const data = await apiFetch<Extract<CopySignInLinkResponse, { success: true }>>(
      '/admin/sign-in-link/copy',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
    const elapsedMs = Math.round(performance.now() - started);

    logSignInLinkCopyInfo('api ok', {
      email,
      traceId,
      elapsedMs,
      linkLength: data.link?.length ?? 0,
    });
    if (elapsedMs > SIGN_IN_LINK_COPY_SLOW_MS) {
      logSignInLinkCopyWarn('slow api', { email, traceId, elapsedMs });
    }

    logUsersRolesDebug('signInLink.copy', `${traceId} ok`, {
      email,
      elapsedMs,
      hasLink: Boolean(data.link),
      linkLength: data.link?.length ?? 0,
      continueUrl: data.continueUrl,
    });

    return {
      success: true,
      message: data.message,
      link: data.link,
      continueUrl: data.continueUrl,
      note: data.note,
    };
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    const message = error instanceof Error ? error.message : 'Failed to generate sign-in link.';
    logSignInLinkCopyWarn('api failed', {
      email,
      traceId,
      elapsedMs,
      message,
    });
    return {
      success: false,
      error: 'REQUEST_FAILED',
      message,
    };
  }
}

export async function requestAdminPrepareSignInEmail(
  email: string
): Promise<PrepareEmailSendResponse> {
  const traceId = createUsersRolesTrace('adminPrepareSignInEmail');
  const started = performance.now();
  try {
    const data = await apiFetch<Extract<PrepareEmailSendResponse, { success: true }>>(
      '/admin/sign-in-link/prepare-email',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
    const elapsedMs = Math.round(performance.now() - started);

    logUsersRolesDebug('signInLink.prepare', `${traceId} ok`, {
      email,
      elapsedMs,
      continueUrl: data.actionCodeSettings?.url,
    });

    return data;
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    const message = error instanceof Error ? error.message : 'Failed to prepare sign-in email.';
    logUsersRolesDebug('signInLink.prepare', `${traceId} failed`, {
      email,
      elapsedMs,
      error: message,
    });
    return {
      success: false,
      error: 'REQUEST_FAILED',
      message,
    };
  }
}

export async function requestAdminConfirmSignInEmail(
  email: string,
  nonce: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
  const traceId = createUsersRolesTrace('adminConfirmSignInEmail');
  const started = performance.now();
  try {
    const data = await apiFetch<{ success?: boolean; message?: string }>(
      '/admin/sign-in-link/confirm-email',
      {
        method: 'POST',
        body: JSON.stringify({ email, nonce }),
      }
    );
    const elapsedMs = Math.round(performance.now() - started);
    logUsersRolesDebug('signInLink.confirm', `${traceId} ok`, { email, elapsedMs });
    return { success: true, message: data.message };
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    const message = error instanceof Error ? error.message : 'Failed to confirm sign-in email.';
    logUsersRolesDebug('signInLink.confirm', `${traceId} failed`, {
      email,
      elapsedMs,
      error: message,
    });
    return {
      success: false,
      error: 'REQUEST_FAILED',
      message,
    };
  }
}

/** Hybrid email path: prepare → Firebase client send → confirm send record. */
export async function requestAdminSendSignInEmail(
  email: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  const traceId = createUsersRolesTrace('adminSendSignInEmail');
  const senderAuth = getSignInLinkSenderAuth?.() ?? auth;
  if (!senderAuth) {
    logUsersRolesDebug('signInLink.send', `${traceId} no auth`, { email });
    return { success: false, error: 'AUTH_UNAVAILABLE', message: 'Firebase Auth is not available.' };
  }

  const prepared = await requestAdminPrepareSignInEmail(email);
  if (!prepared.success) {
    logUsersRolesDebug('signInLink.send', `${traceId} prepare failed`, {
      email,
      error: prepared.error,
      message: prepared.message,
    });
    return prepared;
  }

  try {
    const firebaseStarted = performance.now();
    await withTimeout(
      sendSignInLinkToEmail(senderAuth, prepared.email, prepared.actionCodeSettings),
      SIGN_IN_EMAIL_SEND_TIMEOUT_MS,
      'Timed out sending sign-in email. Try Copy link, or enable Email link in Firebase Console.'
    );
    logUsersRolesDebug('signInLink.send', `${traceId} firebase sendSignInLinkToEmail ok`, {
      email: prepared.email,
      elapsedMs: Math.round(performance.now() - firebaseStarted),
    });
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    const message =
      (error instanceof Error && error.message) ||
      (typeof err.message === 'string' && err.message) ||
      'Firebase could not send the sign-in email.';
    logUsersRolesDebug('signInLink.send', `${traceId} firebase failed`, {
      email,
      code: err.code,
      message,
    });
    logger.error('[sign-in-link] Firebase sendSignInLinkToEmail failed', {
      email,
      code: err.code,
      message,
      continueUrl: prepared.actionCodeSettings?.url,
    });
    return { success: false, error: 'FIREBASE_SEND_FAILED', message };
  }

  const confirmed = await requestAdminConfirmSignInEmail(prepared.email, prepared.nonce);
  logUsersRolesDebug('signInLink.send', `${traceId} complete`, {
    email,
    success: confirmed.success,
    error: confirmed.error,
  });
  return confirmed;
}

export type SelfServePrepareResponse =
  | SignInLinkApiError
  | {
      success: true;
      email: string;
      actionCodeSettings: { url: string; handleCodeInApp: boolean };
      nonce: string;
    };

export async function requestSelfServePrepareSignInEmail(
  email: string,
  options?: { priceId?: string; returnTo?: string },
): Promise<SelfServePrepareResponse> {
  const response = await fetch('/api/auth/send-sign-in-link/prepare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, priceId: options?.priceId, returnTo: options?.returnTo }),
  });

  const data = await parseJsonResponse<SelfServePrepareResponse>(response);

  if (!response.ok) {
    return selfServeErrorFromResponse(response, data, 'Failed to prepare sign-in email.');
  }

  return data;
}

export async function requestSelfServeConfirmSignInEmail(
  email: string,
  nonce: string,
  options?: { priceId?: string },
): Promise<{ success: boolean; message?: string; error?: string }> {
  const response = await fetch('/api/auth/send-sign-in-link/confirm', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, nonce, priceId: options?.priceId }),
  });

  const data = await parseJsonResponse<{ success?: boolean; message?: string; error?: string }>(
    response
  );

  if (!response.ok) {
    return selfServeErrorFromResponse(response, data, 'Failed to confirm sign-in email.');
  }

  return { success: true, message: data.message };
}

/** Self-serve on /login: prepare → Firebase send → confirm (#7308). */
export async function requestSelfServeSendSignInEmail(
  email: string,
  options?: { priceId?: string; returnTo?: string },
): Promise<{ success: boolean; message?: string; error?: string }> {
  if (!auth) {
    return { success: false, error: 'AUTH_UNAVAILABLE', message: 'Firebase Auth is not available.' };
  }

  const prepared = await requestSelfServePrepareSignInEmail(email, options);
  if (!prepared.success) {
    return prepared;
  }

  try {
    await withTimeout(
      sendSignInLinkToEmail(auth, prepared.email, prepared.actionCodeSettings),
      SIGN_IN_EMAIL_SEND_TIMEOUT_MS,
      'Timed out sending sign-in email. Try again in a few minutes or contact your administrator.'
    );
    window.localStorage.setItem('emailForSignIn', prepared.email);
  } catch {
    return {
      success: false,
      error: 'FIREBASE_SEND_FAILED',
      message: SELF_SERVE_GENERIC_FAILURE_MESSAGE,
    };
  }

  return requestSelfServeConfirmSignInEmail(prepared.email, prepared.nonce, options);
}

export async function fetchLastSignInLinkSent(
  emails: string[]
): Promise<Record<string, string | null>> {
  if (emails.length === 0) {
    return {};
  }

  const traceId = createUsersRolesTrace('fetchLastSignInLinkSent');
  const started = performance.now();
  try {
    const data = await apiFetch<{ lastSent?: Record<string, string | null> }>(
      '/admin/sign-in-link/last-sent',
      {
        method: 'POST',
        body: JSON.stringify({ emails }),
      }
    );
    const elapsedMs = Math.round(performance.now() - started);
    const withTimestamp = Object.values(data.lastSent ?? {}).filter(Boolean).length;
    logUsersRolesDebug('signInLink.lastSent', `${traceId} ok`, {
      emailCount: emails.length,
      withTimestamp,
      elapsedMs,
    });
    return data.lastSent ?? {};
  } catch (error) {
    const elapsedMs = Math.round(performance.now() - started);
    logUsersRolesDebug('signInLink.lastSent', `${traceId} failed`, {
      emailCount: emails.length,
      elapsedMs,
      error: error instanceof Error ? error.message : String(error),
    });
    return {};
  }
}
